import {skipToken} from "@reduxjs/toolkit/query";
import {
  extractErrorMessage,
  isUserProcessNoRecords,
  useCancelUserProcessMutation,
  useGetUserProcessDiagnosticsQuery,
  useMarkAllUserProcessesSeenMutation,
  useMarkUserProcessSeenMutation,
  useRetryUserProcessMutation,
  UserProcess,
} from "@ots-frontend/ots-data";
import {ElementType, useCallback, useEffect, useState} from "react";
import {OtsActionButton, OtsActionLink} from "../components/OtsButton";
import {OtsToastProvider, OtsToastTone, useOptionalOtsToast, useOtsToast} from "../components/OtsToast";
import {DiagnosticsDialog} from "../diagnostics/DiagnosticsDialog";
import {createDiagnosticNavigation, DiagnosticNavigation, DiagnosticNavigationTarget} from "../diagnostics/navigation";
import {RichErrorDialog} from "../diagnostics/RichErrorDialog";
import {DiagnosticRun, RichMessage} from "../diagnostics/types";
import {OtsBrand, OtsColorSchemePreference} from "../theme/OtsTheme";
import {summarizeProcesses} from "./process-ui";
import {ProcessRowAction} from "./ProcessRow";
import {ProcessTray} from "./ProcessTray";
import {useUserProcesses, UseUserProcessesOptions} from "./useUserProcesses";

export type ConnectedProcessTrayProps = {
  /** Renders the trigger as another component, e.g. catalyst's NavbarItem. */
  triggerAs?: ElementType;
  iconOnly?: boolean;
  /** Keep a live stream open. Off in stories. */
  live?: boolean;
  /** Show a toast when a process finishes while this is mounted. */
  notify?: boolean;
  brand?: OtsBrand;
  colorScheme?: OtsColorSchemePreference;
  /** Swaps the stream, for tests. */
  connect?: UseUserProcessesOptions["connect"];
  /**
   * What the screen behind the tray can reach, asked each time details open because the person may have moved to
   * another screen since. Lets a ticket in a diagnostic jump to its row in the open driver activity grid.
   */
  getNavigationTarget?: () => DiagnosticNavigationTarget | null | undefined;
  /** Brings the record with this id into view on the host screen. */
  onNavigate?: (id: string) => void;
  /**
   * Saving a run of findings to the host's status bar, the dialog's "Keep in status bar". The id of the run the
   * host is keeping now, if any; leave all three out and the dialog offers no way to keep a run.
   */
  getPinnedDiagnosticsId?: () => string | null | undefined;
  /** Keep this run so the person can come back to it after closing the dialog. */
  onPinDiagnostics?: (id: string, run: DiagnosticRun) => void;
  onUnpinDiagnostics?: (id: string) => void;
  /** Most toasts shown at once when this tray brings its own toast provider. */
  toastLimit?: number;
};

const finishedToast = (process: UserProcess): {tone: OtsToastTone; title: string} | undefined => {
  const hasWarnings = process.warningCount > 0;
  if (isUserProcessNoRecords(process)) return {tone: hasWarnings ? "warning" : "info", title: `${process.name} found no records`};

  switch (process.status) {
    case "SUCCEEDED":
      return hasWarnings
        ? {tone: "warning", title: `${process.name} is ready with warnings`}
        : {tone: "success", title: `${process.name} is ready`};
    case "PARTIAL":
      return {tone: "warning", title: `${process.name} finished with problems`};
    case "FAILED":
      return {tone: "danger", title: `${process.name} failed`};
    default:
      return undefined;
  }
};

/** Whether a process saved something to show: report warnings, or the findings or message behind a failure. */
const hasProcessDetails = (process: UserProcess) =>
  process.hasDiagnostics || (process.status === "FAILED" && process.failureMessage != null);

type ProcessDetailsDialogProps = {
  process: UserProcess;
  diagnostics?: DiagnosticRun;
  diagnosticsFailed: boolean;
  navigation?: DiagnosticNavigation;
  /** The host is keeping this process's findings in its status bar. */
  pinned?: boolean;
  onPin?: (run: DiagnosticRun) => void;
  onUnpin?: () => void;
  onClose: () => void;
};

/**
 * The same dialogs the waiting report showed: diagnostics for warnings or a data failure, and the structured
 * message for a refusal.
 */
const ProcessDetailsDialog = ({
  process,
  diagnostics,
  diagnosticsFailed,
  navigation,
  pinned,
  onPin,
  onUnpin,
  onClose,
}: ProcessDetailsDialogProps) => {
  const failed = process.status === "FAILED";

  // Mounted closed and opened a frame later. Mounting a dialog already open, in the same render that closes the
  // tray popover, paints it fully shown for a frame before Headless UI starts its enter transition from closed --
  // which reads as the dialog showing, vanishing and showing again.
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  if (failed && !process.hasDiagnostics) {
    return <RichErrorDialog
      open={open}
      title={`${process.name} failed`}
      message={process.failureMessage as RichMessage | undefined}
      fallbackText={process.summary}
      canNavigate={navigation?.canNavigate}
      onNavigate={navigation && ((ref) => {
        // the record is behind this dialog; unlike the diagnostics dialog, this one doesn't close itself
        onClose();
        navigation.onNavigate(ref);
      })}
      onClose={onClose}/>;
  }

  return <DiagnosticsDialog
    open={open}
    title={failed ? `${process.name} failed` : `${process.name} warnings`}
    run={diagnostics}
    additionalText={diagnosticsFailed ? "The details couldn't be loaded. Try again in a moment." : undefined}
    canNavigate={navigation?.canNavigate}
    onNavigate={navigation?.onNavigate}
    pinned={pinned}
    // nothing to keep until the findings have loaded
    onPin={onPin && diagnostics ? () => onPin(diagnostics) : undefined}
    onUnpin={onUnpin && diagnostics ? onUnpin : undefined}
    onClose={onClose}/>;
};

const ConnectedProcessTrayInner = ({
  triggerAs,
  iconOnly,
  live = true,
  notify = true,
  brand,
  colorScheme,
  connect,
  getNavigationTarget,
  onNavigate,
  getPinnedDiagnosticsId,
  onPinDiagnostics,
  onUnpinDiagnostics,
}: ConnectedProcessTrayProps) => {
  const toast = useOtsToast();
  const [navigationTarget, setNavigationTarget] = useState<DiagnosticNavigationTarget | null | undefined>();
  const [busy, setBusy] = useState<Record<string, ProcessRowAction | undefined>>({});
  const [reviewing, setReviewing] = useState<UserProcess | undefined>();
  const [pinnedId, setPinnedId] = useState<string | null | undefined>();

  const [cancelProcess] = useCancelUserProcessMutation();
  const [retryProcess] = useRetryUserProcessMutation();
  const [markSeen] = useMarkUserProcessSeenMutation();
  const [markAllSeen] = useMarkAllUserProcessesSeenMutation();

  // a refusal with only a structured message has no diagnostics to fetch
  const diagnostics = useGetUserProcessDiagnosticsQuery(reviewing?.hasDiagnostics ? reviewing.id : skipToken);

  const openDetails = useCallback((process: UserProcess) => {
    setNavigationTarget(getNavigationTarget?.());
    setPinnedId(getPinnedDiagnosticsId?.());
    setReviewing(process);
    void markSeen(process.id);
  }, [markSeen, getNavigationTarget, getPinnedDiagnosticsId]);

  const onFinished = useCallback((process: UserProcess) => {
    if (!notify) return;
    const notice = finishedToast(process);
    if (!notice) return;

    const file = process.files.find((candidate) => candidate.downloadUrl);
    const action = file || hasProcessDetails(process)
      ? <div className="flex flex-wrap items-center gap-2">
        {file && <OtsActionLink size="compact" variant="tonal" href={file.downloadUrl} download={file.fileName} target="_blank" rel="noopener noreferrer">
          Download
        </OtsActionLink>}
        {hasProcessDetails(process) && <OtsActionButton size="compact" variant="secondary" onClick={() => openDetails(process)}>
          {process.status === "FAILED" ? "See details" : "Review warnings"}
        </OtsActionButton>}
      </div>
      : undefined;
    const warnings = process.warningCount > 0
      ? `${process.warningCount.toLocaleString()} ${process.warningCount === 1 ? "warning" : "warnings"}`
      : undefined;

    toast.show({
      id: `process-${process.id}`,
      tone: notice.tone,
      title: notice.title,
      description: [process.description, process.summary, warnings].filter(Boolean).join(" · ") || undefined,
      action,
      // failures stay until dismissed, so they aren't missed
      duration: process.status === "FAILED" ? 0 : undefined,
    });
  }, [notify, toast, openDetails]);

  const {processes, loading, error, reload, streamState} = useUserProcesses({live, onFinished, connect});

  const run = async (process: UserProcess, action: ProcessRowAction, call: () => Promise<unknown>, failure: string) => {
    setBusy((current) => ({...current, [process.id]: action}));
    try {
      await call();
    } catch (err) {
      toast.show({tone: "danger", title: failure, description: extractErrorMessage(err, "Try again in a moment.")});
    } finally {
      setBusy((current) => ({...current, [process.id]: undefined}));
    }
  };

  const onOpenChange = (open: boolean) => {
    // results count as seen once the person has looked at the tray
    if (!open && summarizeProcesses(processes).unseenCount > 0) void markAllSeen();
  };

  return <>
    <ProcessTray
      processes={processes}
      loading={loading}
      error={error}
      onReload={reload}
      liveUpdatesPaused={live && streamState === "retrying"}
      busy={busy}
      triggerAs={triggerAs}
      iconOnly={iconOnly}
      brand={brand}
      colorScheme={colorScheme}
      onOpenChange={onOpenChange}
      onMarkAllSeen={() => void markAllSeen()}
      onCancel={(process) => run(process, "cancel", () => cancelProcess(process.id).unwrap(), `Couldn't cancel ${process.name}`)}
      onRetry={(process) => run(process, "retry", () => retryProcess(process.id).unwrap(), `Couldn't retry ${process.name}`)}
      onShowDetails={openDetails}/>
    {reviewing && <ProcessDetailsDialog
      process={reviewing}
      diagnostics={diagnostics.data as DiagnosticRun | undefined}
      diagnosticsFailed={diagnostics.isError}
      navigation={createDiagnosticNavigation(navigationTarget, onNavigate)}
      pinned={pinnedId === reviewing.id}
      onPin={onPinDiagnostics && ((run) => onPinDiagnostics(reviewing.id, run))}
      onUnpin={onUnpinDiagnostics && (() => onUnpinDiagnostics(reviewing.id))}
      onClose={() => setReviewing(undefined)}/>}
  </>;
};

/**
 * The person's processes in the navbar: a trigger that shows whether work is running or new, a tray to download,
 * cancel, retry and review warnings, and a toast when something finishes. Uses the nearest toast provider, or its
 * own when there is none (e.g. when GWT mounts the navbar on its own).
 */
export const ConnectedProcessTray = (props: ConnectedProcessTrayProps) => {
  const toast = useOptionalOtsToast();
  return toast
    ? <ConnectedProcessTrayInner {...props}/>
    : <OtsToastProvider brand={props.brand} colorScheme={props.colorScheme} limit={props.toastLimit}>
      <ConnectedProcessTrayInner {...props}/>
    </OtsToastProvider>;
};
